import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Buildings, Storefront, Briefcase, WhatsappLogo, ArrowRight } from "@phosphor-icons/react";
import PhotoCarousel from "@/components/public/PhotoCarousel";
import { useMedia } from "@/context/MediaContext";
import { openWhatsApp, WHATSAPP_LEASE_MESSAGE } from "@/lib/api";
import { trackLeaseIntentClick } from "@/lib/gtag";

const NAV = [
  { label: "O Shopping", desc: "Estrutura e diferenciais", to: "/shopping", icon: Buildings },
  { label: "Lojas & Espaços", desc: "Unidades disponíveis", to: "/lojas", icon: Storefront },
  { label: "VM Corporate", desc: "Escritórios prontos a partir de R$ 22/hora", to: "/corporate", icon: Briefcase },
];

// Renders hero title where *text* becomes gold italic
function HeroTitle({ text }) {
  const parts = (text || "").split("*");
  return (
    <>
      {parts.map((p, idx) =>
        idx % 2 === 1 ? (
          <span key={idx} className="gold-text italic">{p}</span>
        ) : (
          <span key={idx}>{p}</span>
        )
      )}
    </>
  );
}

export default function Home() {
  const { home_carousel, home_hero_title } = useMedia();
  const slides = (home_carousel || []).filter((s) => s && s.image);

  return (
    <section className="relative min-h-[100svh] flex flex-col justify-end" data-testid="home-hero">
      <PhotoCarousel images={slides} />
      <div className="absolute inset-0 bg-gradient-to-t from-vm-bg via-vm-bg/55 to-black/40 z-10" />

      <div className="relative z-20 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-28 sm:pb-20 pt-28">
        <motion.span initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="block text-xs tracking-[0.3em] uppercase text-vm-gold mb-4">
          VM Piabetá Shopping Center
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.8 }} className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl" data-testid="home-hero-title">
          <HeroTitle text={home_hero_title} />
        </motion.h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="group flex items-center justify-between gap-3 rounded-2xl bg-white/8 backdrop-blur-md border border-white/15 px-5 py-4 hover:bg-vm-gold hover:border-vm-gold transition-colors duration-300" data-testid={`home-nav-${n.to.replace("/", "")}`}>
              <div className="flex items-center gap-3">
                <n.icon size={26} weight="duotone" className="text-vm-gold group-hover:text-vm-bg transition-colors shrink-0" />
                <div className="text-left">
                  <span className="block text-sm font-medium group-hover:text-vm-bg transition-colors">{n.label}</span>
                  <span className="block text-xs text-vm-fg/70 group-hover:text-vm-bg/80 transition-colors">{n.desc}</span>
                </div>
              </div>
              <ArrowRight size={18} className="text-vm-fg/60 group-hover:text-vm-bg transition-colors" />
            </Link>
          ))}
          <button
            onClick={() => { trackLeaseIntentClick(); openWhatsApp(WHATSAPP_LEASE_MESSAGE); }}
            className="group flex items-center justify-between gap-3 rounded-2xl bg-vm-gold border border-vm-gold px-5 py-4 lg:col-span-1 sm:col-span-2"
            data-testid="home-nav-whatsapp"
          >
            <div className="flex items-center gap-3">
              <WhatsappLogo size={26} weight="fill" className="text-vm-bg shrink-0" />
              <div className="text-left">
                <span className="block text-sm font-semibold text-vm-bg">Aluguel de Lojas</span>
                <span className="block text-xs text-vm-bg/75">A partir de R$ 800 mensais</span>
              </div>
            </div>
            <ArrowRight size={18} className="text-vm-bg" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
